import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

// Receiving responsible employees (histories list)
export const fetchBusinessSectorResponsibles = createAsyncThunk(
  "business_sector_responsibles/fetchAll",
  async ({ page = 1, email = "", employee_id = "" }, { rejectWithValue }) => {
    try {
      const params = { page };
      const emailTrim = typeof email === "string" ? email.trim() : "";
      const idTrim = employee_id != null ? String(employee_id).trim() : "";
      if (emailTrim) params.email = emailTrim;
      if (idTrim) params.employee_id = idTrim;

      const response = await axios.get(
        `${BASE_URL}/business-sector-receiving-responsible-histories/employees`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
            Lang: i18n.language,
          },
          params,
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed load data";
      return rejectWithValue(message);
    }
  }
);

// Fetch all jobs
export const fetchAllJobs = createAsyncThunk(
  "business_sector_responsibles/fetchAllJobs",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/settings/all-jobs`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load jobs";
      return rejectWithValue(message);
    }
  }
);

// Add business sector responsible
export const addBusinessSectorResponsible = createAsyncThunk(
  "business_sector_responsibles/add",
  async (formData, { rejectWithValue }) => {
    try {
      const data = new FormData();
      
      // Append all form fields
      if (formData.business_id) data.append("business_id", formData.business_id);
      if (formData.name) data.append("name", formData.name);
      if (formData.job_dept_id) data.append("job_dept_id", formData.job_dept_id);
      if (formData.phone) data.append("phone", formData.phone);
      if (formData.email) data.append("email", formData.email);

      const response = await axios.post(`${BASE_URL}/business-sector-responsibles`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add responsible";
      return rejectWithValue(message);
    }
  }
);

const BusinessSectorResponsiblesSlice = createSlice({
  name: "business_sector_responsibles",
  initialState: {
    business_sector_responsibles: [],
    jobs: [],
    isLoading: false,
    error: null,
    success: null,
    pagination: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBusinessSectorResponsibles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBusinessSectorResponsibles.fulfilled, (state, action) => {
        state.isLoading = false;
        const inner = action.payload?.data;
        state.business_sector_responsibles = Array.isArray(inner?.data) ? inner.data : [];
        state.pagination = inner?.pagination ?? null;
      })
      .addCase(fetchBusinessSectorResponsibles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      // Fetch all jobs
      .addCase(fetchAllJobs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllJobs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.jobs = action.payload || [];
      })
      .addCase(fetchAllJobs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load jobs";
      })
      // Add responsible
      .addCase(addBusinessSectorResponsible.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(addBusinessSectorResponsible.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Responsible added successfully";
      })
      .addCase(addBusinessSectorResponsible.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add responsible";
      });
  },
});

export const { clearState } = BusinessSectorResponsiblesSlice.actions;
export default BusinessSectorResponsiblesSlice.reducer;
